/**
 * Auth context. Wraps the SPA, exposes `useAuth()`, performs login/logout,
 * and runs the bootstrap `GET /api/v1/me` so the rest of the tree knows
 * who the user is.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { api, ApiError } from '@/lib/api'
import {
  clearSession,
  getSessionGeneration,
  getStoredSession,
  hasSession,
  isCurrentSession,
  patchStoredUser,
  setSession,
  type AuthUser,
} from '@/lib/auth'

interface Me extends AuthUser {}

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  login: (email: string, password: string, remember?: boolean) => Promise<void>
  /**
   * Creates the account on the auth service. The session is only established
   * once the address has been confirmed, so the result says whether the user
   * still has to check their inbox.
   */
  register: (
    input: { email: string; password: string; firstName: string; lastName: string },
    remember?: boolean,
  ) => Promise<RegisterResult>
  /** Asks the auth service to email a password reset link. */
  forgotPassword: (email: string) => Promise<void>
  /** Completes a password reset with the token from the reset email. */
  resetPassword: (token: string, newPassword: string) => Promise<void>
  /** Redeems the email confirmation token from the confirmation email. */
  confirmEmail: (token: string) => Promise<{ email: string }>
  /** Finishes a Google sign-in that came back through `/auth/google/callback`. */
  completeGoogleSignIn: (code: string, state: string) => Promise<{ user: AuthUser; next: string }>
  /** Ends the session and loads `redirectTo` (default `/login`). */
  logout: (redirectTo?: string) => Promise<void>
  refresh: () => Promise<void>
  /** PATCH /me — updates alias/avatar/themeId and persists locally. */
  updateProfile: (patch: {
    alias?: string | null
    customAvatarUrl?: string | null
    themeId?: string | null
  }) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface LoginResponse {
  cookieSession: true
  user: AuthUser
}

interface GoogleSignInResponse extends LoginResponse {
  /** Where the server was told to send the user after the round trip. */
  next: string
  /** The remember choice made before the round trip. */
  remember: boolean
}

/** What the register call tells the page to do next. */
export interface RegisterResult {
  /** The account exists but the address still has to be confirmed. */
  pendingVerification: boolean
  email: string
}

/** The account was created, but no session is issued until it is confirmed. */
interface PendingVerification {
  pendingVerification: true
  email: string
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredSession()?.user ?? null)
  const [loading, setLoading] = useState<boolean>(true)
  // Bumped for every login/logout so late network responses cannot restore an
  // older account after the user has changed authentication scope.
  const authOperation = useRef(0)

  // Verify the existing session on mount by calling /me.
  useEffect(() => {
    let cancelled = false
    const session = getStoredSession()
    const generation = getSessionGeneration()
    const operation = authOperation.current
    // A cookie session may exist without a browser cache, so /me always runs.
    const isStale = () =>
      cancelled ||
      operation !== authOperation.current ||
      (session ? !isCurrentSession(session, generation) : getSessionGeneration() !== generation)
    api<Me>('/api/v1/me')
      .then((me) => {
        if (isStale()) return
        setUser(me)
        setSession(session ? { ...session, user: me } : { cookieSession: true, accessToken: '', refreshToken: '', user: me })
      })
      .catch((e) => {
        if (cancelled || operation !== authOperation.current) return
        if (isStale() && hasSession()) return
        if (e instanceof ApiError && e.status === 401) {
          clearSession()
          setUser(null)
        }
      })
      .finally(() => {
        if (!cancelled && operation === authOperation.current) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(async (email: string, password: string, remember = false) => {
    const operation = ++authOperation.current
    const res = await api<LoginResponse>('/api/session/login', { method: 'POST', body: { email, password, remember } })
    if (operation !== authOperation.current) throw new DOMException('Login was superseded', 'AbortError')
    setSession({ accessToken: '', refreshToken: '', cookieSession: true, user: res.user }, { remember })
    setUser(res.user)
  }, [])

  const register = useCallback(
    async (
      input: { email: string; password: string; firstName: string; lastName: string },
      remember = false,
    ): Promise<RegisterResult> => {
      const operation = ++authOperation.current
      const res = await api<LoginResponse | PendingVerification>('/api/session/register', {
        method: 'POST',
        body: { ...input, remember },
      })
      if (operation !== authOperation.current) throw new DOMException('Registration was superseded', 'AbortError')
      if ('pendingVerification' in res) {
        // No session: the address still has to be confirmed.
        return { pendingVerification: true, email: res.email || input.email }
      }
      setSession({ accessToken: '', refreshToken: '', cookieSession: true, user: res.user }, { remember })
      setUser(res.user)
      return { pendingVerification: false, email: res.user.email }
    },
    [],
  )

  const forgotPassword = useCallback(async (email: string) => {
    await api<{ message: string }>('/api/session/forgot-password', { method: 'POST', body: { email } })
  }, [])

  const resetPassword = useCallback(async (token: string, newPassword: string) => {
    await api<{ message: string }>('/api/session/reset-password', {
      method: 'POST',
      body: { token, newPassword },
    })
  }, [])

  const confirmEmail = useCallback(async (token: string) => {
    return api<{ email: string }>('/api/session/confirm-email', { method: 'GET', query: { token } })
  }, [])

  const completeGoogleSignIn = useCallback(async (code: string, state: string) => {
    const operation = ++authOperation.current
    const res = await api<GoogleSignInResponse>('/api/session/oauth/callback', {
      method: 'POST',
      body: { code, state },
    })
    if (operation !== authOperation.current) throw new DOMException('Sign-in was superseded', 'AbortError')
    setSession({ accessToken: '', refreshToken: '', cookieSession: true, user: res.user }, { remember: res.remember })
    setUser(res.user)
    return { user: res.user, next: res.next }
  }, [])

  const logout = useCallback(async (redirectTo = '/login') => {
    authOperation.current += 1
    await api('/api/session/logout', { method: 'POST' })
    clearSession()
    setUser(null)
    setLoading(false)
    if (typeof window !== 'undefined') location.assign(redirectTo)
  }, [])

  const refresh = useCallback(async () => {
    const me = await api<Me>('/api/v1/me')
    setUser(me)
  }, [])

  const updateProfile = useCallback(
    async (patch: {
      alias?: string | null
      customAvatarUrl?: string | null
      themeId?: string | null
    }) => {
      const me = await api<Me>('/api/v1/me', { method: 'PATCH', body: patch })
      patchStoredUser(me)
      setUser(me)
    },
    [],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      login,
      register,
      forgotPassword,
      resetPassword,
      confirmEmail,
      completeGoogleSignIn,
      logout,
      refresh,
      updateProfile,
    }),
    [
      user,
      loading,
      login,
      register,
      forgotPassword,
      resetPassword,
      confirmEmail,
      completeGoogleSignIn,
      logout,
      refresh,
      updateProfile,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}